import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { useP2PConfig } from "@shared/src/hooks/useP2PConfig";
import Erc20Permit from "@shared/src/lib/abis/Erc20Permit";
import { useReadContracts } from "wagmi";
import { useBalance as useWagmiBalance } from "wagmi";
import { formatUnits } from "viem";
import { getTokenIcon, TokenBalance, useAllBalancesResult } from "@shared/src/hooks/useSingleBalance";

interface UseBalancesProps {
  enabled?: boolean;
}

// Renamed from useBalances to useAllBalances
export function useAllBalances({ enabled = true }: UseBalancesProps): useAllBalancesResult {
  const activeAccount = useAuthStore(state => state.activeAccount);
  const activeChain = useHostStore(state => state.activeChain);
  const { tokens: p2pTokens } = useP2PConfig({ tokens: true });

  // Only filter tokens if we have an active chain
  const tokens = activeChain?.id ? p2pTokens?.filter(t => t.networkId === activeChain.id) : [];

  const nativeSymbol = activeChain?.nativeCurrency?.symbol || "ETH";

  // Determine if hooks should be enabled
  const hooksEnabled = enabled && !!activeAccount;

  // Create contracts array if account exists
  const contracts = activeAccount
    ? tokens?.map(token => ({
        address: token.address,
        abi: Erc20Permit,
        functionName: "balanceOf",
        args: [activeAccount.address],
        chainId: activeChain?.id,
      }))
    : [];

  const decimalsContracts = activeAccount
    ? tokens?.map(token => ({
        address: token.address,
        abi: Erc20Permit,
        functionName: "decimals",
        args: [],
        chainId: activeChain?.id,
      }))
    : [];

  // Token balances hook
  const {
    data: rawBalances,
    isLoading: tokensLoading,
    queryKey,
    refetch: refetchTokens,
    isFetching,
    error,
  } = useReadContracts({
    contracts,
    query: {
      enabled: hooksEnabled && !!contracts,
    },
  });

  const {
    data: decimalsData,
    isLoading: decimalsLoading,
    isFetching: isFetchingDecimals,
    error: decimalsError,
  } = useReadContracts({
    contracts: decimalsContracts,
    query: {
      enabled: hooksEnabled && !!contracts,
    },
  });
  // Native token balance hook
  const {
    data: ethBalance,
    isLoading: ethLoading,
    refetch: refetchEth,
  } = useWagmiBalance({
    address: activeAccount?.address,
    chainId: activeChain?.id,
    query: {
      enabled: hooksEnabled,
    },
  });

  // Set default values
  let balances: TokenBalance[] | null = null;
  let total = "";
  let formattedTotal = "";
  const isLoading = tokensLoading || ethLoading;

  // Process data if account exists
  if (activeAccount && rawBalances && decimalsData) {
    // Process token balances
    const tokenBalances =
      rawBalances
        .map((balance, index) => {
          const rawAmount = balance.result
            ? parseFloat(formatUnits(balance.result as bigint, decimalsData[index].result as number)).toFixed(6)
            : "0.000000";
          const tokenSymbol = tokens?.[index].symbol.toLowerCase().includes(nativeSymbol.toLowerCase())
            ? nativeSymbol
            : tokens?.[index].symbol.toLowerCase().includes("usdt")
            ? "USDT"
            : tokens?.[index].symbol.toLowerCase().includes("usdc")
            ? "USDC"
            : "Unknown";

          // ETH value is $3000, others are $1
          const usdRate = tokenSymbol === nativeSymbol ? 3000 : 1;
          const usdValue = (parseFloat(rawAmount) * usdRate).toFixed(2);

          if (!tokens?.[index].address) return null;

          return {
            icon: getTokenIcon(tokenSymbol),
            name: tokenSymbol,
            subtext: tokenSymbol,
            amount: `${rawAmount} ${tokenSymbol}`,
            value: `$${usdValue}`,
            rawBalance: balance.result as bigint,
            address: tokens[index].address,
            decimals: decimalsData[index].result as number,
            rawAmount,
          };
        })
        .filter((balance): balance is TokenBalance => balance !== null) || [];

    // Combine token balances with ETH balance
    balances = tokenBalances;
  }

  // Create ETH balance object if available
  const ethBalanceObject = ethBalance
    ? {
        icon: getTokenIcon(nativeSymbol),
        name: nativeSymbol,
        subtext: nativeSymbol,
        amount: `${parseFloat(ethBalance.formatted).toFixed(9)} ${nativeSymbol}`,
        value: `$${(parseFloat(ethBalance.formatted) * 3000).toFixed(2)}`,
        rawBalance: ethBalance.value,
        address: "0x" as `0x${string}`,
        decimals: 18,
        rawAmount: parseFloat(ethBalance.formatted).toFixed(2),
      }
    : null;

  if (ethBalanceObject) {
    balances = [ethBalanceObject, ...(balances || [])];
  }

  // Calculate total value if we have balances
  if (balances && balances.length > 0) {
    const totalValue = balances.reduce((sum, balance) => {
      return sum + parseFloat(balance.value.replace("$", ""));
    }, 0);

    total = totalValue.toFixed(2);
    formattedTotal = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(totalValue);
  } else {
    total = "0.000000";
    formattedTotal = "$0.000000";
  }

  const refetch = async () => {
    await Promise.all([refetchTokens(), refetchEth()]);
  };

  return {
    balances,
    total,
    formattedTotal,
    isLoading,
    queryKey,
    refetch,
    isFetching,
  };
}
